import { Flex, theme } from 'antd';
import DateRangeFilter from '@/components/cards/FilterCard/dateRangeFilter/DateRangeFilter';
import ShowsFilterEcho from '@/components/cards/FilterCard/showsFilter/ShowsFilterEcho';
import { useDerivedNetworkContext } from '@/context';
import useBreakpoint from '@/hooks/useBreakpoint';
import type { DateRange, ShowFilter } from '@/types';

type ModalBodyProps = {
  showShowsFilter: boolean;
  showDateRangeFilter: boolean;
  modalDateRange: DateRange;
  modalSelectedShow: ShowFilter;
  handleDateRangeApply: (dateRange: DateRange) => void;
  children: (modalDateRange: DateRange, modalSelectedShow: ShowFilter) => React.ReactNode;
  onDateRangeOpen?: () => void;
  onDateRangePresetSelect?: (preset: string) => void;
  onShowFilterOpen?: () => void;
  onShowFilterApply: (selectedShow: ShowFilter) => void;
};

/**
 * This component is used to render the body of a modal.
 * @param showShowsFilter - Whether to show the shows filter.
 * @param showDateRangeFilter - Whether to show the date range filter.
 * @param modalDateRange - The date range of the modal.
 * @param modalSelectedShow - The selected show of the modal.
 * @param onShowFilterApply - The function to handle the selected show apply.
 * @param handleDateRangeApply - The function to handle the date range apply.
 * @param children - The children of the modal.
 * @param onDateRangeOpen - The function to handle the date range open.
 * @param onDateRangePresetSelect - The function to handle the date range preset select.
 * @param onShowFilterOpen - The function to handle the shows filter open.
 * @param onShowFilterApply - The function to handle the shows filter apply.
 * @returns The modal body component.
 */
const ModalBodyEcho = ({
  showShowsFilter,
  showDateRangeFilter,
  modalDateRange,
  modalSelectedShow,
  handleDateRangeApply,
  onDateRangeOpen,
  onDateRangePresetSelect,
  onShowFilterOpen,
  onShowFilterApply,
  children,
}: ModalBodyProps) => {
  const { token } = theme.useToken();
  const { isDesktop } = useBreakpoint();
  const { shows } = useDerivedNetworkContext();

  return (
    <>
      {(showShowsFilter || showDateRangeFilter) && (
        <Flex
          vertical={!isDesktop}
          gap={token.marginMD}
          style={{
            padding: token.paddingLG,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          {showShowsFilter && (
            <ShowsFilterEcho
              shows={shows}
              selectedShow={modalSelectedShow}
              onApply={onShowFilterApply}
              onOpen={onShowFilterOpen}
            />
          )}
          {showDateRangeFilter && (
            <DateRangeFilter
              dateRange={{
                from: modalDateRange.from,
                to: modalDateRange.to,
              }}
              onApply={(from, to) => handleDateRangeApply({ from, to })}
              onOpen={onDateRangeOpen}
              onPresetSelect={onDateRangePresetSelect}
            />
          )}
        </Flex>
      )}
      {children(modalDateRange, modalSelectedShow)}
    </>
  );
};

export default ModalBodyEcho;
